import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, BookOpen, Clock } from "lucide-react";
import type { Book } from "@shared/schema";
import { Link } from "wouter";
import { updatePageSEO } from "@/lib/seo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import React, { useState, useRef } from "react";
import { SplitTestPopup, type SplitTestPopupRef } from "@/components/SplitTestPopup";

import BrokenThings_Cover from "@assets/BrokenThings_Cover.webp";

const Books = () => {
  const popupRef = useRef<SplitTestPopupRef>(null);
  useEffect(() => {
    updatePageSEO({
      title: "Broken Things by Diane Corso | Horror & Thriller Books",
      description: "Explore the spine-chilling horror novels of Diane Corso, including the award-winning Broken Things. Discover supernatural thrillers and dark fiction that will haunt you.",
      keywords: "Broken Things, Diane Corso books, Diane Corso Author, horror novels, supernatural thriller, Skin and Bones, dark fiction books",
      ogImage: "https://www.dianecorsofiction.com/attached_assets/broken things book rev (1)_1756509124921.webp",
      canonical: "https://www.dianecorsofiction.com/books"
    });
  }, []);

  const { data: books = [], isLoading } = useQuery<Book[]>({
    queryKey: ["/api/books"],
  });

  const [selectedBook, setSelectedBook] = useState<Book | null>(null);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blood-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gothic-white">Loading books...</p>
        </div>
      </div>
    );
  }

  const publishedBooks = books.filter(book => book.isComingSoon !== "true");
  const comingSoonBooks = books.filter(book => book.isComingSoon === "true" && book.title !== "Skin and Bones 2nd Edition");

  const truncateText = (text: string, maxLength = 200) => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="min-h-screen pt-16">
      <section className="py-20 bg-gothic-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-cinzel font-bold text-blood-red mb-6">Books</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Explore Diane Corso's collection of spine-chilling tales that blur the line between nightmare and reality
            </p>
            <div className="w-24 h-1 bg-blood-red mx-auto mt-6"></div>
          </div>

          {/* Published Books */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {publishedBooks.map((book) => (
              <Card key={book.id} className="bg-gothic-charcoal border-gothic-gray hover:transform hover:scale-105 transition-all duration-300 overflow-hidden shadow-2xl">
                {book.slug === 'upcoming-1' ? (
                  <div className="w-full h-80 bg-gradient-to-br from-gothic-gray to-gothic-charcoal flex items-center justify-center">
                    <div className="text-center">
                      <Clock className="w-16 h-16 text-blood-red mb-4 mx-auto" />
                      <p className="text-xl font-playfair text-gothic-white">Coming Soon</p>
                    </div>
                  </div>
                ) : (
                  <img
                    src="/images/broken-things-cover.webp"
                    alt={`${book.title} book cover`}
                    className="w-auto h-auto"
                    data-testid={`book-cover-${book.slug}`}
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <CardContent className="p-6">
                  <h3 className="text-2xl font-playfair font-bold mb-3 text-gothic-white">{book.title}</h3>
                  {book.slug === 'upcoming-1' && (
                    <p className="text-gray-300 mb-4 leading-relaxed">Work in Progress</p>
                  )}
                  {book.publishYear && book.slug !== 'upcoming-1' && (
                    <p className="text-sm text-gray-400 mb-3">Published {book.publishYear}</p>
                  )}
                  {book.slug !== 'upcoming-1' && (
                    <>
                      <p className="text-gray-300 mb-4 leading-relaxed">
                        {truncateText(book.synopsis)}
                        {book.synopsis.length > 200 && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <button
                                className="text-blood-red hover:text-gothic-white ml-2 font-semibold transition-colors duration-300"
                                onClick={() => setSelectedBook(book)}
                                aria-label={`Read full synopsis of ${book.title}`}
                              >
                                Read Full Synopsis of {book.title}
                              </button>
                            </DialogTrigger>
                            <DialogContent className="bg-gothic-charcoal border-gothic-gray text-gothic-white max-w-2xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="text-2xl font-playfair text-blood-red mb-4">
                                  {book.title}
                                </DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <img
                                  src={book.slug === 'broken-things' ? "/attached_assets/Broken Things Thumbnail_1756942116379.webp" : book.slug === 'skin-and-bones' ? "/attached_assets/Skin and Bones Thumbnail_1756940553754.webp" : book.coverImage}
                                  alt={`${book.title} book cover`}
                                  className="w-full h-80 object-cover rounded-lg"
                                  loading="lazy"
                                  decoding="async"
                                  width="600"
                                  height="800"
                                />
                                <div className="text-gray-300 leading-relaxed">
                                  {book.synopsis.split('\n\n').map((paragraph, index) => (
                                    <p key={index} className="mb-4 last:mb-0">
                                      {paragraph}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                      </p>
                    </>
                  )}

                  {book.rating && (
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <div className="flex text-yellow-400">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-4 h-4 ${i < Math.floor(parseFloat(book.rating!)) ? 'fill-current' : ''}`} />
                          ))}
                        </div>
                        <span className="ml-2 text-sm text-gray-400">{book.rating}/5 ({book.reviewCount} reviews)</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    {book.slug === 'upcoming-1' ? (
                      <Button
                        onClick={() => popupRef.current?.openManual()}
                        className="w-full border-blood-red text-blood-red hover:bg-blood-red hover:text-white"
                        variant="outline"
                        data-testid={`notify-${book.slug}`}
                        aria-label={`Get notified when ${book.title} is released`}
                      >
                        Get Notified
                      </Button>
                    ) : (
                      book.purchaseLinks?.map((link, index) => (
                        <Button
                          key={index}
                          asChild
                          className={index === 0
                            ? "w-full bg-blood-red hover:bg-deep-red text-white"
                            : "w-full border border-gothic-silver hover:border-blood-red text-gothic-white hover:text-blood-red bg-transparent hover:bg-transparent"
                          }
                          data-testid={`buy-${book.slug}-${link.platform.toLowerCase().replace(/\s/g, '-')}`}
                          aria-label={`Purchase ${book.title} on ${link.platform}`}
                        >
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            <BookOpen className="mr-2" size={16} />
                            Buy on {link.platform}
                          </a>
                        </Button>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Coming Soon Section */}
          {comingSoonBooks.length > 0 && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-cinzel font-bold text-blood-red mb-4">Coming Soon</h2>
                <div className="w-16 h-1 bg-blood-red mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {comingSoonBooks.map((book) => (
                  <Card key={book.id} className="bg-gothic-charcoal border-gothic-gray hover:transform hover:scale-105 transition-all duration-300 overflow-hidden shadow-2xl">
                    <div className="w-full h-80 bg-gradient-to-br from-gothic-gray to-gothic-charcoal flex items-center justify-center relative">
                      <div className="text-center">
                        <Clock className="w-16 h-16 text-blood-red mb-4 mx-auto" />
                        <p className="text-xl font-playfair text-gothic-white">Coming Soon</p>
                      </div>
                      <div className="absolute top-4 right-4 bg-blood-red text-white px-3 py-1 rounded-full text-sm font-semibold">
                        New
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-2xl font-playfair font-bold mb-3 text-gothic-white">{book.title}</h3>
                      <Button
                        variant="outline"
                        className="w-full border-blood-red text-blood-red hover:bg-blood-red hover:text-white"
                        data-testid={`notify-${book.slug}`}
                        aria-label={`Get notified when ${book.title} is released`}
                      >
                        Get Notified
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <SplitTestPopup ref={popupRef} />
    </div>
  );
};

export default Books;